/**
 * Part of a formatted number, modelled on Intl.NumberFormat.formatToParts.
 *
 * `prefix` and `suffix` types are custom, since there is no direct support for
 * currency in this formatter.
 */
export type FormatNumberPart = {
	type: 'minusSign' | 'integer' | 'group' | 'decimal' | 'fraction' | 'prefix' | 'infix' | 'suffix'
	value: string
}

export type FormatNumberOptions = {
	/** Number of decimals included. Defaults to 0. */
	precision?: number
	/** Prefix added before the number */
	prefix?: string
	/**
	 * Infix added between minus-sign and number (equivalent to prefix on
	 * positive numbers). Whitespace is automatically trimmed from the start if
	 * the infix is first or is joined to a prefix ending in whitespace.
	 */
	infix?: string
	/** Suffix added before the number */
	suffix?: string
	/** Character used for minus. Defaults to `-` (hyphen/minus). */
	minus?: string
	/** Decimal separator. Defaults to `.` (period). */
	decimal?: string
	/** Thousand grouping separator. */
	group?: string
}

const nonWhitespaceEndning = /\S$/

export const formatNumberToParts = (
	value: number,
	{
		precision = 0,
		prefix = '',
		infix = '',
		suffix = '',
		minus = '-',
		decimal = '.',
		group = '',
	}: FormatNumberOptions = {},
) => {
	const result: Array<FormatNumberPart> = []
	const isNegative = value < 0
	if (prefix) result.push({ type: 'prefix', value: prefix })
	if (isNegative) result.push({ type: 'minusSign', value: minus })
	// Trim whitespace if infix first, or is joined to prefix ending in whitespace
	if (infix)
		result.push({ type: 'infix', value: isNegative || nonWhitespaceEndning.test(prefix) ? infix : infix.trimStart() })

	// Integer and fraction part of the number
	const [integer, fraction] = Math.abs(value).toFixed(precision).split('.')

	if (group) {
		// Split integer into groups of 3 if necessary
		const start = integer.length % 3 || 3
		result.push({ type: 'integer', value: integer.substring(0, start) })
		for (let i = start; i < integer.length; i += 3) {
			result.push({ type: 'group', value: group })
			result.push({ type: 'integer', value: integer.substring(i, i + 3) })
		}
	} else {
		// Just push the entire integer
		result.push({ type: 'integer', value: integer })
	}

	// Add the fraction if necessary
	if (fraction) {
		result.push({ type: 'decimal', value: decimal })
		result.push({ type: 'fraction', value: fraction })
	}

	if (suffix) result.push({ type: 'suffix', value: suffix })

	return result
}

/** Format a number with exact control of the output */
export const formatNumber = (value: number, options?: FormatNumberOptions) =>
	formatNumberToParts(value, options).reduce((current, { value }) => current + value, '')
